import type { ReactNode } from 'react';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { useContentGateActive } from '~/modules-rocco/content-gate/use-content-gate-active';
import { DirectoryIntro } from './DirectoryIntro';
import { DirectoryPromoBanner } from './DirectoryPromoBanner';

/** A full-width module injected into the directory grid at a fixed cadence. */
export interface DirectoryGridInjection {
    key: string;
    node: ReactNode;
    /**
     * Show at the first injection point only, instead of cycling back round.
     * For modules that make their point once and become noise on repeat.
     */
    once?: boolean;
}

/**
 * The modules injected into the directory grid, cycled in order at the grid's
 * injection cadence. Extend this list to add future injectable modules (e.g.
 * ads).
 *
 * Each module shows once: Get Matched at the first break, then the sign-in
 * module at the second for a logged-out reader, and the grid runs
 * uninterrupted after that. A signed-in reader has nothing to sign in for,
 * so the sign-in module is not in the list at all.
 *
 * So does a reader whose grid ends in the content gate: that panel is the same
 * ask, made harder, a couple of rows further down. Two of them in one screen is
 * one too many, so the gate takes the slot and Get Matched keeps the rest.
 */
export const useDirectoryGridInjections = (): DirectoryGridInjection[] => {
    const { isLoggedIn } = useUserLoggedIn();
    const contentGateActive = useContentGateActive();

    const injections: DirectoryGridInjection[] = [
        {
            key: 'get-matched',
            // Said once. The lead already offers the brief at the top of the
            // page, and a reader who has scrolled past two of them is not
            // going to be moved by a third.
            once: true,
            node: (
                <DirectoryIntro
                    variant="grid-injection"
                    eyebrow="Find the perfect solution"
                    heading="Can't find what you're after? Get Matched lets you describe what you need and have matching suppliers come to you."
                    entryPoint="GridPrompt"
                />
            ),
        },
    ];

    if (!isLoggedIn && !contentGateActive) {
        // Once, as Get Matched is: a reader who scrolled past the first
        // sign-in ask is browsing, and a second one at every break is nagging.
        injections.push({
            key: 'unlock',
            once: true,
            node: <DirectoryPromoBanner />,
        });
    }

    return injections;
};
